// we are editing this.touchState class variable so we reassign params in this file
/* eslint-disable no-param-reassign */

const MINIMUM_PROPORTIONAL_DISTANCE_TO_SET_INTENDED_DIRECTION = 2;
const MINIMUM_PROPORTIONAL_VELOCITY_TO_SET_INTENDED_DIRECTION = 3;

/**
 * check to see if swiping is in x direction
 * @param {number} normalizedVelocityX - calculated velocity
 * @param {number} normalizedVelocityY - calculated velocity
 * @param {number} totalX - calculated change
 * @param {number} totalY - calculated change
 * @returns {boolean} x direction only
 */
const isOnlySwipingX = function isOnlySwipingX(
  normalizedVelocityX,
  normalizedVelocityY,
  totalX,
  totalY
) {
  return (
    normalizedVelocityX / normalizedVelocityY >
      MINIMUM_PROPORTIONAL_VELOCITY_TO_SET_INTENDED_DIRECTION ||
    totalX / totalY >= MINIMUM_PROPORTIONAL_DISTANCE_TO_SET_INTENDED_DIRECTION
  );
};

/**
 * check to see if swiping is in y direction
 * @param {number} normalizedVelocityX - calculated velocity
 * @param {number} normalizedVelocityY - calculated velocity
 * @param {number} totalX - calculated change
 * @param {number} totalY - calculated change
 * @returns {boolean} y direction only
 */
const isOnlySwipingY = function isOnlySwipingY(
  normalizedVelocityX,
  normalizedVelocityY,
  totalX,
  totalY
) {
  return (
    normalizedVelocityY / normalizedVelocityX >
      MINIMUM_PROPORTIONAL_VELOCITY_TO_SET_INTENDED_DIRECTION ||
    totalY / totalX >= MINIMUM_PROPORTIONAL_DISTANCE_TO_SET_INTENDED_DIRECTION
  );
};

/**
 * create swipe event
 * @param {object} state - touchState class variable
 * @param {object} e - event object
 * @returns {object} event
 */
const createSwipe = function createSwipe(state, e) {
  // The first event in a series of touches
  if (!state.swipePositions.length) {
    return {
      deltaX: 0,
      deltaY: 0,
      isOnlySwipingX: false,
      isOnlySwipingY: false,
      normalizedVelocityX: 0,
      normalizedVelocityY: 0,
      pageX: e.pageX,
      pageY: e.pageY,
      preventFocus: e.preventFocus,
      preventDefault: e.preventDefault,
      timestamp: Date.now(),
      totalDeltaX: 0,
      totalDeltaY: 0,
      velocityX: 0,
      velocityY: 0
    };
  }
  const previous = state.swipePositions[0];
  const deltaX = e.pageX - previous.pageX;
  const deltaY = e.pageY - previous.pageY;
  const timestamp = e.timestamp || Date.now();
  const timechange = timestamp - previous.timestamp || 1;
  const velocityX = (e.pageX - previous.pageX) / timechange;
  const velocityY = (e.pageY - previous.pageY) / timechange;
  const normalizedVelocityX = (velocityX + previous.velocityX) / 2;
  const normalizedVelocityY = (velocityY + previous.velocityY) / 2;

  state.totalX += Math.abs(deltaX);
  state.totalY += Math.abs(deltaY);
  state.totalDistance += state.totalX + state.totalY;

  return {
    accelerationX: (velocityX - previous.velocityX) / timechange,
    accelerationY: (velocityY - previous.velocityY) / timechange,
    deltaX,
    deltaY,
    isOnlySwipingX:
      previous.isOnlySwipingX ||
      (!previous.isOnlySwipingY &&
        isOnlySwipingX(normalizedVelocityX, normalizedVelocityY, state.totalX, state.totalY)),
    isOnlySwipingY:
      previous.isOnlySwipingY ||
      (!previous.isOnlySwipingX &&
        isOnlySwipingY(normalizedVelocityX, normalizedVelocityY, state.totalX, state.totalY)),
    normalizedVelocityX,
    normalizedVelocityY,
    pageX: e.pageX,
    pageY: e.pageY,
    preventFocus: e.preventFocus,
    preventDefault: e.preventDefault,
    timestamp,
    velocityX,
    velocityY
  };
};

/**
 * create browser event
 * @param {object} state - touchState class variable
 * @param {object} e - event
 * @param {object} domElement - react ref
 * @param {boolean} isFinalEvent - last event
 * @returns {object} event
 */
const createEvent = function createEvent(state, e, domElement, isFinalEvent) {
  let pageX;
  let pageY;

  if (state.isTouching && e.touches) {
    pageX = e.touches[0] ? e.touches[0].pageX : state.swipePositions[0].pageX;
    pageY = e.touches[0] ? e.touches[0].pageY : state.swipePositions[0].pageY;
  } else {
    ({ pageX, pageY } = e);
  }

  /**
   * prevent default func
   */
  const preventDefault = () => {
    if (e.pageX !== null) {
      e.preventDefault();
    }

    if (isFinalEvent === true) {
      state.preventDefaultClick = true;
    }
  };

  /**
   * prevent focus
   */
  const preventFocus = () => {
    state.preventFocus = true;
  };

  return {
    originalEvent: e,
    button: e.button,
    currentTarget: domElement,
    pageX,
    pageY,
    preventDefault,
    preventFocus,
    target: e.target,
    touches: e.touches || [],
    which: e.which,
    ctrlKey: e.ctrlKey
  };
};

export { createEvent, createSwipe };
